import { useEffect, useState } from "react";
import api from "../services/api";
import {
  atualizarCidade,
  getCidadeAdmin,
  listCidadesAdmin,
} from "../services/cidades";
import FormActions from "../components/Admin/FormActions/FormActions";
import { SuccessDialog } from "../components/Admin/Dialogs";
import { getImageUrl } from "../utils/media";
import { UploadImageIcon } from "../assets/UploadImageIcon";
import quemSomosFallback from "../assets/images/quem-somos-hero-shaped.png";
import metodologiaFallback from "../assets/images/metodologia-hero-shaped.png";
import blogFallback from "../assets/images/blog-hero.png";
import contatoFallback from "../assets/images/contato-hero-shaped.png";
import apoieFallback from "../assets/images/apoie-hero-shaped.png";
import { getStaticRegionalHub } from "./RegionalHub";
import "./AdminCabecalho.css";

const EMPTY_IMAGE = { url: "", public_id: "" };
const HEADER_FALLBACKS = {
  "quem-somos": quemSomosFallback,
  metodologia: metodologiaFallback,
  "onde-estamos": blogFallback,
  "para-empresas": metodologiaFallback,
  blog: blogFallback,
  transparencia: blogFallback,
  contato: contatoFallback,
  apoie: apoieFallback,
};

const isVideoUrl = (url) => /youtube\.com|youtu\.be|instagram\.com/i.test(String(url || ""));
const getCityId = (city) => city?._id || city?.id || "";
const hasPersistedCityId = (city) => {
  const id = String(getCityId(city));
  return Boolean(id) && !id.startsWith("fallback-");
};

function getCityHeroUrl(city) {
  const slide = city?.slideshow?.find((item) => item?.url && !isVideoUrl(item.url));
  return getImageUrl(slide?.url || slide);
}

function normalizeCityHeader(city) {
  const staticHub = getStaticRegionalHub(city?.slug);
  return {
    ...city,
    section: "city",
    pageKey: `city:${city.slug || getCityId(city)}`,
    title: city?.presentation?.title || city?.name || city?.city || "Cidade",
    imageUrl: getCityHeroUrl(city) || staticHub?.heroImageUrl || "",
    cityName: city?.city || city?.name || "",
    staticHub,
  };
}

function buildCityHeaderPayload(city, draft) {
  const slideshow = Array.isArray(city.slideshow) ? [...city.slideshow] : [];
  const imageSlide = slideshow.findIndex((item) => item?.url && !isVideoUrl(item.url));
  const nextImageSlide = {
    ...(imageSlide >= 0 ? slideshow[imageSlide] : {}),
    url: String(draft.imageUrl || "").trim(),
    alt: String(draft.title || city.name || city.city || "").trim(),
    order: imageSlide >= 0 ? slideshow[imageSlide].order || imageSlide : 0,
  };

  if (draft.imageUrl) {
    if (imageSlide >= 0) {
      slideshow[imageSlide] = nextImageSlide;
    } else {
      slideshow.unshift(nextImageSlide);
    }
  }

  return {
    presentation: {
      ...(city.presentation || {}),
      title: String(draft.title || "").trim(),
      description: city.presentation?.description || city.description || "",
      subtitle: city.presentation?.subtitle || "",
    },
    slideshow: slideshow.map((slide, index) => ({
      url: slide.url,
      alt: slide.alt || "",
      order: index,
    })),
  };
}

export default function AdminCabecalho() {
  const [headers, setHeaders] = useState([]);
  const [cityHeaders, setCityHeaders] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [previewUrls, setPreviewUrls] = useState({});

  const allHeaders = [...headers, ...cityHeaders];
  const selected = allHeaders.find((item) => item.pageKey === selectedKey);
  const current = editing ? draft : selected;

  useEffect(() => {
    api.get("/config/cabecalhos")
      .then(({ data }) => {
        setHeaders(Array.isArray(data) ? data : []);
        setSelectedKey(data?.[0]?.pageKey || "");
      })
      .catch(() => setError("Não foi possível carregar os cabeçalhos."));

    listCidadesAdmin()
      .then(async (cities) => {
        const completeCities = await Promise.all(
          cities.map((city) =>
            hasPersistedCityId(city)
              ? getCidadeAdmin(getCityId(city)).catch(() => city)
              : city,
          ),
        );
        setCityHeaders(completeCities.map(normalizeCityHeader));
      })
      .catch(() => undefined);
  }, []);

  const selectPage = (pageKey) => {
    setSelectedKey(pageKey);
    setEditing(false);
    setDraft(null);
    setError("");
  };

  const uploadImage = async (event, item) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";

    if (!["image/jpeg", "image/png"].includes(file.type) || file.size > 15 * 1024 * 1024) {
      setError("A imagem deve ser JPG ou PNG e ter no máximo 15 MB.");
      return;
    }

    const localPreview = URL.createObjectURL(file);
    setPreviewUrls((currentValue) => ({ ...currentValue, [item.pageKey]: localPreview }));
    startEditing(item);
    setUploadingKey(item.pageKey);

    const formData = new FormData();
    formData.append("image", file);
    try {
      const { data } = await api.post("/media/upload", formData);
      setDraft((value) => item.section === "city"
        ? { ...value, imageUrl: data.url }
        : { ...value, image: data },
      );
      setPreviewUrls((currentValue) => ({ ...currentValue, [item.pageKey]: getImageUrl(data) }));
      setError("");
    } catch {
      setError("Não foi possível enviar a imagem.");
    } finally {
      setUploadingKey("");
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (!current) return;
    if (uploadingKey) {
      setError("Aguarde o envio da imagem terminar antes de salvar.");
      return;
    }

    setError("");
    if (!String(draft?.title || "").trim()) {
      setError("Informe o título antes de salvar.");
      return;
    }

    setSaving(true);
    try {
      if (current.section === "city") {
        if (!hasPersistedCityId(current)) {
          throw new Error("Esta cidade está em modo local. Conecte o backend e o banco para salvar.");
        }

        const data = await atualizarCidade(
          getCityId(current),
          buildCityHeaderPayload(current, draft),
        );
        setCityHeaders((items) =>
          items.map((item) => item.pageKey === selectedKey ? normalizeCityHeader(data) : item),
        );
      } else {
        const { data } = await api.put(`/config/cabecalhos/${selectedKey}`, {
          ...draft,
          title: String(draft.title || "").trim(),
        });
        setHeaders((items) => items.map((item) => item.pageKey === selectedKey ? data : item));
      }
      setDraft(null);
      setEditing(false);
      setSuccess("Alterações salvas com sucesso!");
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || "Não foi possível salvar o cabeçalho.");
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (item = selected) => {
    setSelectedKey(item?.pageKey || "");
    setDraft(
      item?.section === "city"
        ? { ...item, imageUrl: item.imageUrl || "" }
        : { ...item, image: item?.image || EMPTY_IMAGE },
    );
    setEditing(true);
    setError("");
  };

  const getPreviewUrl = (item) =>
    previewUrls[item?.pageKey] ||
    (item?.section === "city" ? item.imageUrl : getImageUrl(item?.image)) ||
    HEADER_FALLBACKS[item?.pageKey] ||
    null;

  const renderCard = (item) => {
    const active = item.pageKey === selectedKey;
    const editingThis = active && editing;
    const itemTitle = editingThis ? draft.title : item.title;
    const uploadingThis = uploadingKey === item.pageKey;

    return (
      <article
        key={item.pageKey}
        className={`admin-cabecalho__card${active ? " active" : ""}`}
      >
        {editingThis ? (
          <label className="admin-cabecalho__title-field">
            <span>Título</span>
            <input
              type="text"
              value={draft.title}
              onChange={(event) => setDraft((value) => ({ ...value, title: event.target.value }))}
              required
            />
          </label>
        ) : (
          <h2>{itemTitle}</h2>
        )}
        {item.section === "city" && (
          <p className="admin-cabecalho__subtitle">Onde Estamos - {item.cityName}</p>
        )}
        <button
          type="button"
          className="admin-cabecalho__image-button"
          onClick={() => selectPage(item.pageKey)}
          aria-label={`Selecionar ${itemTitle}`}
        >
          {getPreviewUrl(editingThis ? draft : item) ? (
            <img src={getPreviewUrl(editingThis ? draft : item)} alt={`Capa de ${itemTitle}`} />
          ) : (
            <span>Sem imagem cadastrada</span>
          )}
        </button>
        {active && (
          <form className="admin-cabecalho__card-actions" onSubmit={save}>
            <label
              className="admin-cabecalho__edit-button"
              aria-label={`Trocar capa de ${itemTitle}`}
              title="Trocar imagem"
            >
              <UploadImageIcon />
              <input
                type="file"
                accept="image/jpeg,image/png"
                onChange={(event) => uploadImage(event, editingThis ? draft : item)}
              />
            </label>
            {editingThis ? (
              <FormActions
                editing={editing}
                onCancel={() => { setDraft(null); setEditing(false); setError(""); }}
                onSave={save}
                saving={saving || uploadingThis}
                saveType="submit"
                disabled={uploadingThis}
                savingLabel={uploadingThis ? "Enviando..." : "Salvando..."}
                className="admin-cabecalho__form-actions"
              />
            ) : (
              <FormActions
                editing={false}
                onEdit={() => startEditing(item)}
                className="admin-cabecalho__form-actions"
              />
            )}
          </form>
        )}
      </article>
    );
  };

  return (
    <main className="admin-cabecalho">
      <header className="admin-cabecalho__header">
        <h1>EDITAR SEÇÃO</h1>
        <p>Cabeçalho</p>
      </header>

      {error && <p className="admin-cabecalho__error" role="alert">{error}</p>}

      <div className="admin-cabecalho__layout">
        <div className="admin-cabecalho__cards">
          <h2 className="admin-cabecalho__group-title">Páginas</h2>
          {headers.map(renderCard)}
          {cityHeaders.length > 0 && (
            <>
              <h2 className="admin-cabecalho__group-title">Cidades</h2>
              {cityHeaders.map(renderCard)}
            </>
          )}
        </div>
      </div>

      <SuccessDialog open={Boolean(success)} message={success} onClose={() => setSuccess("")} />
    </main>
  );
}
